/* eslint-disable jsx-a11y/label-has-for */

import PropTypes from 'prop-types';
import classnames from 'classnames';
import Checkmark from '../../../Checkmark';

const hasWarning = ({ touched, error, warning }) =>
  (touched && error) || (touched && warning);

const Radio = ({
  arbitraryComponent,
  classNames,
  extra,
  input,
  isRequired,
  isSelected,
  isTruncated,
  label,
  id,
  type,
  meta: { touched, error, warning },
}) => {
  const hasWarningClass = hasWarning({ touched, error, warning });
  const hasLogo = !!extra.logo;
  const hasCheckmark = isSelected && !hasLogo;

  const radioContainerClasses = {
    'delicateShadow': true,
    'b--light-blue': isSelected,
    'b--red': hasWarningClass,
    'b--white': !hasWarningClass && !isSelected,
    'bg-white': true,
    'h-100': true,
    'ba': true,
    'br2': true,
    'radio': true,
    'relative': true,
  };

  const radioClasses = {
    'b--transparent': true,
    'h-100': true,
    'input-reset': true,
    'top-0': true,
    'trade-gothic-regular': true,
    'w-100': true,
    'absolute': true,
    'ba': true,
    'f6': true,
    'pa4': true,
    [classNames]: !!classNames,
  };

  const durationClasses = {
    'black-50': !isSelected,
    'black-70': isSelected,
    'db': true,
    'f6': true,
    'mt2': true,
  };

  const titleClasses = {
    'black-50': !isSelected,
    'h-100': true,
    'items-center': hasLogo,
    'db': true,
    'f4': true,
    'flex': hasLogo,
    'ph3': true,
    'pv3': true,
  };

  const labelClasses = {
    'h-100': true,
    'w-100': true,
    'absolute': true,
    'pointer': true,
  };

  return (
    <div className="input h-100 pb2 pt1" data-experiment={id}>
      <div className={classnames(radioContainerClasses)}>
        <label
          htmlFor={id}
          style={{ fontSize: 0 }}
          className={classnames(labelClasses)}
        >
          {label}
          {extra.price && <span> ({extra.price})</span>}
        </label>
        <div className={classnames(titleClasses)}>
          <div className="ph1">
            {hasCheckmark && (
              <span className="mr3">
                <Checkmark
                  style={{ verticalAlign: 'top', marginTop: '5px' }}
                  color="light-blue"
                />
              </span>
            )}
            {hasLogo && (
              <span className="dib mr3 w2">
                <img className="w2 v-mid" src={extra.logo} alt={label} />
              </span>
            )}
            <span
              style={{ maxWidth: 'calc(100% - 30px)' }}
              className={classnames('ttu dib trade-gothic-bold', {
                truncate: isTruncated,
              })}
            >
              {label}
              {extra.price && <span> ({extra.price})</span>}
            </span>
            {extra.duration && (
              <span className={classnames(durationClasses)}>
                {extra.duration}
              </span>
            )}
            {arbitraryComponent && arbitraryComponent}
          </div>
        </div>
        <input
          {...input}
          id={id}
          type={type}
          name={input.name}
          required={isRequired}
          className={classnames(radioClasses)}
        />
      </div>
    </div>
  );
};

Radio.defaultProps = {
  isRequired: false,
  options: [],
  classNames: null,
  isSelected: false,
  extra: {},
  arbitraryComponent: null,
  isTruncated: false,
};

Radio.propTypes = {
  arbitraryComponent: PropTypes.element,
  isRequired: PropTypes.bool,
  isTruncated: PropTypes.bool,
  isSelected: PropTypes.bool,
  input: PropTypes.object.isRequired, // eslint-disable-line
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['radio']).isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object),
  classNames: PropTypes.string,
  extra: PropTypes.shape({
    price: PropTypes.string,
    duration: PropTypes.string,
    logo: PropTypes.string,
  }),
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
    warning: PropTypes.string,
  }).isRequired,
};

export default Radio;
